/**
 * Doors Cards Component
 * Fetches doors data via GraphQL and manages filtering/pagination
 */

export function doorsCards(initialData = {}) {
    return {
        // Configuration
        gqlToken: initialData.gqlToken || '',
        limit: initialData.limit ? parseInt(initialData.limit) : Infinity,
        itemsToShow: initialData.limit ? parseInt(initialData.limit) : Infinity,
        relatedIds: initialData.relatedIds || [],
        s3: 'https://cf.pcaproducts.com/images/doors/',
        colorClasses: {
            'wh': 'bg-white checked:!bg-white',
            'iv': 'bg-ivory checked:!bg-ivory',
            'sa': 'bg-sandstone checked:!bg-sandstone',
            'cl': 'bg-clay checked:!bg-clay',
            'br': 'bg-bronze checked:!bg-bronze',
            'bl': 'bg-black checked:!bg-black',
            'bw': 'bg-bw checked:!bg-bw'
        },

        // State
        doors: [],
        filteredDoors: [],
        totalDoors: 0,
        isLoading: false,
        error: null,

        // Filters
        filters: {
            style: [],
            modelName: [],
            modelNumber: []
        },

        // Filter options (populated from GraphQL)
        styleOptions: [],
        modelNameOptions: [],
        modelNumberOptions: [],

        // Initialize
        async init() {
            await this.fetchDoors();
        },

        // Fetch doors from GraphQL
        async fetchDoors() {
            this.isLoading = true;
            this.error = null;

            // Validate token
            if (!this.gqlToken || this.gqlToken.trim() === '') {
                this.error = 'GraphQL token is missing. Please set DOORS_GQL_TOKEN in your environment variables.';
                this.isLoading = false;
                return;
            }

            try {
                const query = `
                    query GetDoors($section: [String], $orderBy: String) {
                        entries(
                            section: $section
                            orderBy: $orderBy
                            hideFromPcaSite: false
                        ) {
                            id
                            title
                            url
                            slug
                            ... on screenDoor_Entry {
                                modelName
                                popularDoors
                                availableColors
                                featuredImage {
                                    url
                                    title
                                    width
                                    height
                                }
                                collection {
                                    id
                                    title
                                }
                                styles {
                                    slug
                                    title
                                }
                            }
                        }
                    }
                `;

                const variables = {
                    section: 'doors',
                    orderBy: 'popularDoors desc, modelName asc',
                };

                const response = await fetch('/api', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                        'Authorization': `Bearer ${this.gqlToken}`
                    },
                    body: JSON.stringify({
                        query,
                        variables
                    })
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data = await response.json();

                if (data.errors) {
                    throw new Error(data.errors[0].message);
                }

                this.doors = data.data.entries || [];

                if (Array.isArray(this.relatedIds) && this.relatedIds.length) {
                    const idOrder = this.relatedIds.map(id => String(id));
                    const doorMap = new Map(
                        this.doors.map(door => [String(door.id), door])
                    );
                    this.doors = idOrder
                        .map(id => doorMap.get(id))
                        .filter(Boolean);
                }

                this.totalDoors = this.doors.length;

                // Normalize data format
                this.doors.forEach(door => {
                    // Normalize featuredImage (asset field returns array, get first item)
                    if (door.featuredImage && Array.isArray(door.featuredImage)) {
                        door.featuredImage = door.featuredImage.length > 0 ? door.featuredImage[0] : null;
                    }

                    // Normalize availableColors format (handle both string array and object array)
                    if (door.availableColors && Array.isArray(door.availableColors)) {
                        door.availableColors = door.availableColors.map(color => {
                            // If it's already an object with value, return as is
                            if (typeof color === 'object' && color !== null && color.value) {
                                return color;
                            }
                            // If it's a string, wrap it in an object
                            return { value: color };
                        });
                    }
                });

                // Extract filter options
                this.extractFilterOptions();

                // Apply initial filters
                this.applyFilters();

            } catch (error) {
                console.error('Error fetching doors:', error);
                this.error = error.message;
                this.doors = [];
            } finally {
                this.isLoading = false;
            }
        },

        // Extract filter options from doors data
        extractFilterOptions() {
            const styleMap = new Map();
            const modelNameSet = new Set();
            const modelNumberMap = new Map();

            this.doors.forEach(door => {
                // Extract styles
                if (door.styles) {
                    door.styles.forEach(style => {
                        if (!styleMap.has(style.slug)) {
                            styleMap.set(style.slug, {
                                value: style.slug,
                                label: style.title
                            });
                        }
                    });
                }

                // Extract model names
                if (door.modelName) {
                    modelNameSet.add(door.modelName);
                }

                // Extract model numbers (titles)
                if (!modelNumberMap.has(door.slug)) {
                    modelNumberMap.set(door.slug, {
                        value: door.slug,
                        label: door.title
                    });
                }
            });

            this.styleOptions = Array.from(styleMap.values()).sort((a, b) =>
                a.label.localeCompare(b.label)
            );
            this.modelNameOptions = Array.from(modelNameSet).sort().map(name => ({
                value: name,
                label: name
            }));
            this.modelNumberOptions = Array.from(modelNumberMap.values()).sort((a, b) =>
                a.label.localeCompare(b.label)
            );
        },

        // Apply filters
        applyFilters() {
            const hasFilters = this.filters.style.length ||
                             this.filters.modelName.length ||
                             this.filters.modelNumber.length;

            if (!hasFilters) {
                this.filteredDoors = [...this.doors];
            } else {
                this.filteredDoors = this.doors.filter(door => {
                    // Style filter
                    const styleMatch = !this.filters.style.length ||
                        (door.styles || []).some(style =>
                            this.filters.style.includes(style.slug)
                        );

                    // Model name filter
                    const modelNameMatch = !this.filters.modelName.length ||
                        (door.modelName && this.filters.modelName.includes(door.modelName));

                    // Model number filter (using slug)
                    const modelNumberMatch = !this.filters.modelNumber.length ||
                        this.filters.modelNumber.includes(door.slug);

                    return styleMatch && modelNameMatch && modelNumberMatch;
                });
            }

            // Reset items to show when filters change (in case user loaded more before filtering)
            this.itemsToShow = this.limit;
        },

        // Get visible doors (filtered + paginated)
        get visibleDoors() {
            return this.filteredDoors.slice(0, this.itemsToShow);
        },

        // Get visible count
        get visibleCount() {
            return Math.min(this.filteredDoors.length, this.itemsToShow);
        },

        // Check if has more items
        get hasMore() {
            return this.itemsToShow < this.filteredDoors.length;
        },

        // Load more items
        loadMore() {
            this.itemsToShow = Math.min(
                this.itemsToShow + this.limit,
                this.filteredDoors.length
            );
        },

        // Reset filters
        resetFilters() {
            this.filters = {
                style: [],
                modelName: [],
                modelNumber: []
            };
            this.itemsToShow = this.limit;
            this.applyFilters();
        },

        // Door image handler
        doorImage(event) {
            const id = event.target.dataset.id;
            const model = event.target.dataset.model;
            const color = event.target.dataset.color;
            const image = document.getElementById(`door-${id}`);

            if (image && model && color) {
                image.src = `${this.s3}${model}_angle_${color}.jpg`;
            }
        }
    };
}

